/**
 * Logger - Simple structured logging utility
 * Supports multiple log levels and optional metadata
 */

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  NONE = 4
}

interface LogEntry {
  timestamp: Date;
  level: LogLevel;
  levelName: string;
  message: string;
  context?: string;
  data?: unknown;
}

class Logger {
  private minLevel: LogLevel;
  private logs: LogEntry[] = [];
  private maxStoredLogs = 500;
  private enableConsole = true;
  private contextColors: Map<string, string> = new Map();

  constructor() {
    this.minLevel = LogLevel.DEBUG;

    // Default color scheme for contexts
    this.contextColors.set('network', '#3498db');
    this.contextColors.set('game', '#2ecc71');
    this.contextColors.set('ui', '#9b59b6');
    this.contextColors.set('scene', '#e67e22');
    this.contextColors.set('packet', '#1abc9c');
    this.contextColors.set('error', '#e74c3c');
  }

  /**
   * Set minimum log level
   */
  setLevel(level: LogLevel): void {
    this.minLevel = level;
  }

  /**
   * Enable/disable console output
   */
  setConsoleOutput(enabled: boolean): void {
    this.enableConsole = enabled;
  }

  /**
   * Set color for a context
   */
  setContextColor(context: string, color: string): void {
    this.contextColors.set(context, color);
  }

  /**
   * Get all stored logs
   */
  getLogs(): ReadonlyArray<LogEntry> {
    return [...this.logs];
  }

  /**
   * Clear stored logs
   */
  clearLogs(): void {
    this.logs = [];
  }

  /**
   * Export logs as JSON string
   */
  exportLogs(): string {
    return JSON.stringify(
      this.logs.map((entry) => ({
        timestamp: entry.timestamp.toISOString(),
        level: entry.levelName,
        message: entry.message,
        context: entry.context,
        data: entry.data
      })),
      null,
      2
    );
  }

  private shouldLog(level: LogLevel): boolean {
    return level >= this.minLevel;
  }

  private formatMessage(levelName: string, message: string, context?: string): string {
    const timestamp = new Date().toISOString();
    const contextStr = context ? `[${context}]` : '';
    return `${timestamp} ${levelName} ${contextStr} ${message}`;
  }

  private storeLog(entry: LogEntry): void {
    this.logs.push(entry);
    if (this.logs.length > this.maxStoredLogs) {
      this.logs.shift();
    }
  }

  private log(level: LogLevel, levelName: string, message: string, context?: string, data?: unknown): void {
    if (!this.shouldLog(level)) return;

    const entry: LogEntry = {
      timestamp: new Date(),
      level,
      levelName,
      message,
      context,
      data
    };

    this.storeLog(entry);

    if (this.enableConsole) {
      const formattedMessage = this.formatMessage(levelName, message, context);
      const color = this.contextColors.get(context || '');

      if (color) {
        console.log(`%c${formattedMessage}`, `color: ${color}`, data ?? '');
      } else {
        console.log(formattedMessage, data ?? '');
      }
    }
  }

  debug(message: string, context?: string, data?: unknown): void {
    this.log(LogLevel.DEBUG, 'DEBUG', message, context, data);
  }

  info(message: string, context?: string, data?: unknown): void {
    this.log(LogLevel.INFO, 'INFO', message, context, data);
  }

  warn(message: string, context?: string, data?: unknown): void {
    this.log(LogLevel.WARN, 'WARN', message, context, data);
  }

  error(message: string, context?: string, data?: unknown): void {
    this.log(LogLevel.ERROR, 'ERROR', message, context, data);
  }

  /**
   * Log a network-related message
   */
  network(message: string, data?: unknown): void {
    this.info(message, 'network', data);
  }

  /**
   * Log a game-related message
   */
  game(message: string, data?: unknown): void {
    this.info(message, 'game', data);
  }

  /**
   * Log a UI-related message
   */
  ui(message: string, data?: unknown): void {
    this.info(message, 'ui', data);
  }

  /**
   * Log a scene-related message
   */
  scene(message: string, data?: unknown): void {
    this.info(message, 'scene', data);
  }

  /**
   * Log packet data (debug level)
   */
  packet(message: string, data?: unknown): void {
    this.debug(message, 'packet', data);
  }

  /**
   * Log an error with stack trace
   */
  exception(message: string, error: Error, context?: string): void {
    this.error(message, context, {
      name: error.name,
      message: error.message,
      stack: error.stack
    });
  }
}

// Global logger instance
export const logger = new Logger();

// Convenience methods
export const debug = (message: string, context?: string, data?: unknown) => logger.debug(message, context, data);
export const info = (message: string, context?: string, data?: unknown) => logger.info(message, context, data);
export const warn = (message: string, context?: string, data?: unknown) => logger.warn(message, context, data);
export const error = (message: string, context?: string, data?: unknown) => logger.error(message, context, data);
export const network = (message: string, data?: unknown) => logger.network(message, data);
export const game = (message: string, data?: unknown) => logger.game(message, data);
export const ui = (message: string, data?: unknown) => logger.ui(message, data);
export const scene = (message: string, data?: unknown) => logger.scene(message, data);
export const packet = (message: string, data?: unknown) => logger.packet(message, data);
